import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AdminArticleForm, {
  ArticleFormData,
} from './components/AdminArticleForm';

import { calculateReadingTime } from './helpers/article.helpers';
import { mapArticleToFormData } from './helpers/article.mapper';
import { useArticle } from './hooks/useArticle';
import { useUpdateArticle } from './hooks/useUpdateArticle';

export default function AdminEditArticle() {
  const navigate = useNavigate();

  const { id } = useParams();
  const { updateArticle, loading } = useUpdateArticle();

  const [initialData, setInitialData] =
    useState<ArticleFormData | null>(null);

  const {
    article,
    loading: articleLoading,
    error,
    refetch,
  } = useArticle(id as string);

  useEffect(() => {
    if (!article) return;

    const mappedData = mapArticleToFormData(article);

    setInitialData(mappedData);
  }, [article]);

  const handleSubmit = async (
    data: ArticleFormData,
    imageFile: File | null,
    translationsPayload?: any[]
  ) => {
    try {
      const esTranslation = translationsPayload?.find(
        (t) => String(t.idioma).toUpperCase() === "ES",
      );
      const readingTime = calculateReadingTime(
        esTranslation?.contenido ?? data.content,
      );

      const payload = {
        categoriaId: Number(data.category),
        grupoArticulo: data.type,
        estado: data.status as 'PUBLICADO' | 'BORRADOR' |'EN_REVISION' | 'APROBADO',
        tiempoDeLectura: readingTime,
        traducciones: translationsPayload || [
          {
            idioma: 'ES',
            titulo: data.title,
            resumen: data.resumen,
            contenido: data.content,
          },
        ],
        // Si no cargó imagen nueva, omitimos el campo 'portada'
        portada: imageFile || undefined,
      };

      if (id) {
        await updateArticle(Number(id), payload);
      }

      navigate('/admin/articulos');
    } catch (error) {
      console.error('Error al editar el artículo:', error);
      throw error;
    }
  };

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '60vh',
          gap: 2,
          px: 3,
          textAlign: 'center',
          border: '1px dashed',
          borderColor: 'error.light',
          borderRadius: '12px',
          bgcolor: 'background.paper',
          py: 4,
          maxWidth: 600,
          mx: 'auto',
        }}
      >
        <Typography variant="h6" color="error" sx={{ fontWeight: 600 }}>
          Error al cargar el artículo
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No pudimos obtener los datos del artículo especificado. Por favor, verifica tu conexión o vuelve al listado.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/admin/articulos')}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Volver al listado
          </Button>
          <Button
            variant="contained"
            onClick={() => refetch()}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Reintentar
          </Button>
        </Box>
      </Box>
    );
  }

  if (articleLoading || !initialData) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '60vh',
          gap: 2,
        }}
      >
        <CircularProgress
          size={48}
          sx={{ color: 'primary.main' }}
        />

        <Typography
          variant="h6"
          color="text.secondary"
        >
          Cargando artículo...
        </Typography>
      </Box>
    );
  }

  return (
    <AdminArticleForm
      initialData={initialData}
      rawArticle={article}
      onSubmit={handleSubmit}
      loading={loading}
      isEdit
    />
  );
}